import { z } from 'zod';
import { evaluateExpression, validateCSVFormat, parseCSVLine } from '../shared';

export const baseSchema = z.object({
  project: z.string().min(1, 'Project is required'),
  title: z.string().min(1, 'Title is required'),

  Lx: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0;
  }, 'Length X must be a valid expression greater than 0'),

  Ly: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0;
  }, 'Length Y must be a valid expression greater than 0'),

  tpl: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0;
  }, 'Plate Thickness must be a valid expression greater than 0'),

  addedWeight: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Added Weight must be a valid expression greater than or equal to 0'),

  damping: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0 && n <= 1;
  }, 'Damping must be a valid expression between 0 and 1'),

  support: z.coerce
    .number()
    .int()
    .refine(n => [1, 2].includes(n), 'Support must be either 1 (Pinned All Around) or 2 (Fixed All Around)'),

  orientation: z.coerce
    .number()
    .int()
    .refine(n => [0, 1].includes(n), 'Orientation must be either 0 (Vertical) or 1 (Horizontal)'),

  fdy: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 1 && n <= 100000;
  }, 'Fdy must be a valid expression between 1 and 100000'),

  fdu: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 1 && n <= 100000;
  }, 'Fdu must be a valid expression between 1 and 100000'),

  E: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 1 && n <= 10 ** 8;
  }, 'E must be a valid expression between 1 and 10^8'),

  density: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0;
  }, 'Density must be a valid expression greater than 0'),

  pressureHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  timeHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  u0: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'u0 must be a valid expression greater than or equal to 0'),

  v0: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'v0 must be a valid expression greater than or equal to 0')
});

/**
 * Full schema with cross-field validation
 */
export const schema = baseSchema.superRefine((data, ctx) => {
  if (parseCSVLine(data.pressureHistory)?.length !== parseCSVLine(data.timeHistory)?.length) {
    const msg = 'Pressure and time histories must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['pressureHistory'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['timeHistory'] });
  }
});

/**
 * Helper types
 */
export type FieldName = keyof z.infer<typeof baseSchema>;
export type FormValues = z.input<typeof schema>; // raw form shape (strings from inputs/selects)
export type ParsedValues = z.output<typeof schema>; // parsed shape (numbers after coercion)
export type FormErrors = Partial<Record<FieldName, string | null>>;

/**
 * Validate a single field
 */
export function validateOneField<K extends FieldName>(key: K, raw: unknown): string | null {
  const fieldSchema = baseSchema.shape[key];
  const res = fieldSchema.safeParse(raw);
  return res.success ? null : (res.error.issues[0]?.message ?? 'Invalid value');
}
